import React, { useRef, useState, useEffect } from "react";
import styled from "@emotion/styled";
import throttle from "lodash/throttle";
import { graphql, useStaticQuery } from "gatsby";

import Layout from "@narative/gatsby-theme-novela/src/components/Layout";
import MDXRenderer from "@narative/gatsby-theme-novela/src/components/MDX";
import Progress from "@narative/gatsby-theme-novela/src/components/Progress";
import TableOfContents from "@narative/gatsby-theme-novela/src/components/TableOfContents";
import Section from "@narative/gatsby-theme-novela/src/components/Section";

import mediaqueries from "@narative/gatsby-theme-novela/src/styles/media";
import { debounce } from "@narative/gatsby-theme-novela/src/utils";

import ArticleAside from "@narative/gatsby-theme-novela/src/sections/article/Article.Aside";
import ArticleHero from "@narative/gatsby-theme-novela/src/sections/article/Article.Hero";
import ArticleControls from "@narative/gatsby-theme-novela/src/sections/article/Article.Controls";
import ArticlesNext from "@narative/gatsby-theme-novela/src/sections/article/Article.Next";
import ArticleSEO from "@narative/gatsby-theme-novela/src/sections/article/Article.SEO";
import ArticleShare from "@narative/gatsby-theme-novela/src/sections/article/Article.Share";
import ArticleFooter from "@narative/gatsby-theme-novela/src/templates/article.footer.template";

import { Template } from "@narative/gatsby-theme-novela/src/types";
import ArticleSections from "../sections/custom/Article.Sections";
import banner_side from "../../../assets/banner_side.png";
import banner_mobile from "../../../assets/banner_mobile.png";
import banner_pc from "../../../assets/banner_pc.png";
import { useMediaQuery } from "react-responsive";

const siteQuery = graphql`
  {
    allSite {
      edges {
        node {
          siteMetadata {
            name
            siteUrl
          }
        }
      }
    }
  }
`;

const Article: Template = ({ pageContext, location }) => {
  const [isStartOfBody, setIsStartOfBody] = useState(false);
  const [isEndOfBody, setIsEndOfBody] = useState(false);
  const contentSectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const checkScroll = () => {
      if (contentSectionRef.current) {
        const rect = contentSectionRef.current.getBoundingClientRect();
        if (rect.top <= 0) {
          setIsStartOfBody(true);
        } else {
          setIsStartOfBody(false);
        }
      }

      if (contentSectionRef.current) {
        const rect = contentSectionRef.current.getBoundingClientRect()
        if (rect.bottom <= window.innerHeight) {
          setIsEndOfBody(true);
        } else {
          setIsEndOfBody(false);
        }
      }
    };

    window.addEventListener('scroll', checkScroll);
    return () => window.removeEventListener('scroll', checkScroll);
  }, []);

  const [hasCalculated, setHasCalculated] = useState<boolean>(false);
  const [contentHeight, setContentHeight] = useState<number>(0);

  const results = useStaticQuery(siteQuery);
  const name = results.allSite.edges[0].node.siteMetadata.name;

  const { article, authors, next } = pageContext;

  const [pathname, setPathname] = useState<string>("");

  const topBannerUrl =
    "https://smartstore.naver.com/sunnynuttyday/products/9653341526?nt_source=wordpress&nt_medium=hitchblog_top&nt_detail=";

  const bottomBannerUrl =
    "https://smartstore.naver.com/sunnynuttyday/products/9653341526?nt_source=wordpress&nt_medium=hitchblog_bottom&nt_detail=";

  const sideBannerUrl =
    "https://smartstore.naver.com/sunnynuttyday/products/9653341526?nt_source=wordpress&nt_medium=hitchblog_side&nt_detail=";

  const getBannerProps = (isTop: boolean, pathname: string) => {
    const bannerUrl = isTop
      ? pathname
        ? topBannerUrl + pathname
        : topBannerUrl
      : pathname
        ? bottomBannerUrl + pathname
        : bottomBannerUrl;
    const bannerStyle = isTop
      ? { width: "100%", maxWidth: "944px" }
      : { width: "100%", maxWidth: "720px" };

    return { bannerUrl, bannerStyle };
  }

  const Desktop = ({ isTop }) => {
    const isDesktop = useMediaQuery({ minWidth: 992 });
    const { bannerUrl, bannerStyle } = getBannerProps(isTop, pathname);
    return isDesktop ? (
      <TopBanner>
        <a href={bannerUrl} target='_blank' rel="sponsored">
          <img src={banner_pc} style={bannerStyle} />
        </a>
      </TopBanner>
    ) : (
      <></>
    );
  };

  const Tablet = ({ isTop }) => {
    const isTablet = useMediaQuery({ minWidth: 768, maxWidth: 991 });
    const { bannerUrl, bannerStyle } = getBannerProps(isTop, pathname);
    return isTablet ? (
      <TopBanner>
        <a
          href={bannerUrl}
          target='_blank'
          rel="sponsored"
        >

          <img src={banner_pc} style={bannerStyle} />
        </a>
      </TopBanner>
    ) : (
      <></>
    );
  };

  const Mobile = ({ isTop }) => {
    const isMobile = useMediaQuery({ maxWidth: 767 });
    const { bannerUrl, bannerStyle } = getBannerProps(isTop, pathname);
    return isMobile ? (
      <TopBanner>
        <a
          href={bannerUrl}
          target='_blank'
          rel="sponsored"
        >
          <img src={banner_mobile} style={{ width: "100%" }} />
        </a>
      </TopBanner>
    ) : (
      <></>
    );
  };

  const IsWide = () => {
    const isWide = useMediaQuery({ minWidth: 1320 });
    return isWide ? (
      <SideBanner style={isStartOfBody
        ? isEndOfBody
          ? { position: 'sticky' }
          : { position: 'fixed' }
        : { position: "absolute" }}>
        <a
          href={pathname ? sideBannerUrl + pathname : sideBannerUrl}
          target='_blank'
          rel="sponsored"
        >
          <img src={banner_side} style={{ width: "100%", maxWidth: "300px" }} />
        </a>
      </SideBanner>
    ) : (
      <></>
    );
  };

  useEffect(() => {
    let path = window.location.pathname;
    let decodePath = decodeURI(decodeURIComponent(path)).slice(1);
    setPathname(decodePath);
  }, []);

  useEffect(() => {
    const calculateBodySize = throttle(() => {
      const contentSection = contentSectionRef.current;

      if (!contentSection) return;

      /**
       * If we haven't checked the content's height before,
       * we want to add listeners to the content area's
       * imagery to recheck when it's loaded
       */
      if (!hasCalculated) {
        const debouncedCalculation = debounce(calculateBodySize);
        const $imgs = contentSection.querySelectorAll("img");

        $imgs.forEach(($img) => {
          // If the image hasn't finished loading then add a listener
          if (!$img.complete) $img.onload = debouncedCalculation;
        });

        // Prevent rerun of the listener attachment
        setHasCalculated(true);
      }

      // Set the height and offset of the content area
      setContentHeight(contentSection.getBoundingClientRect().height);
    }, 20);

    calculateBodySize();
    window.addEventListener("resize", calculateBodySize);

    return () => window.removeEventListener("resize", calculateBodySize);
  }, []);

  return (
    <Layout>
      <ArticleSEO article={article} authors={authors} location={location} />
      <ArticleHero article={article} authors={authors} />
      <ArticleAside contentHeight={contentHeight}>
        <TableOfContents article={article.title} />
      </ArticleAside>
      <ArticleAside contentHeight={contentHeight}>
        <Progress contentHeight={contentHeight} />
      </ArticleAside>
      <MobileControls>
        <ArticleControls />
      </MobileControls>

      <Desktop isTop={true} />
      <Tablet isTop={true} />
      <Mobile isTop={true} />
      <ArticleBody ref={contentSectionRef}>
        <IsWide />
        <MDXRenderer content={article.body}></MDXRenderer>
        {article.section ? <ArticleSections section={article.section} /> : null}
        <Desktop isTop={false} />
        <Tablet isTop={false} />
        <Mobile isTop={false} />
        <ArticleShare />
      </ArticleBody>

      <ArticleFooter pageContext={pageContext} />
      {next.length > 0 && (
        <NextArticle narrow>
          <FooterNext>More articles from {name}</FooterNext>
          <ArticlesNext articles={next} />
          <FooterSpacer />
        </NextArticle>
      )}
    </Layout>
  );
};

export default Article;

const MobileControls = styled.div`
  position: relative;
  padding-top: 60px;
  transition: background 0.2s linear;
  text-align: center;

  ${mediaqueries.tablet_up`
    display: none;
  `}
`;

const ArticleBody = styled.article`
  position: relative;
  padding: 80px 0 35px;
  padding-left: 68px;
  transition: background 0.2s linear;

  code{
    font-family: monospace;
    background-color: #f4f0f0;
    padding: 2px 5px;
    border-radius: 5px;
    font-size: 0.9em;
  }

  ${mediaqueries.desktop`
    padding-left: 53px;
  `}
  
  ${mediaqueries.tablet`
    padding: 70px 0 80px;
  `}

  ${mediaqueries.phablet`
    padding: 60px 0;
  `}
`;

const NextArticle = styled(Section)`
  display: block;
`;

const FooterNext = styled.h3`
  position: relative;
  opacity: 0.25;
  margin-bottom: 100px;
  font-weight: 400;
  color: ${(p) => p.theme.colors.primary};

  ${mediaqueries.tablet`
    margin-bottom: 60px;
  `}

  &::after {
    content: '';
    position: absolute;
    background: ${(p) => p.theme.colors.grey};
    width: ${(910 / 1140) * 100}%;
    height: 1px;
    right: 0;
    top: 11px;

    ${mediaqueries.tablet`
      width: ${(600 / 1140) * 100}%;
    `}

    ${mediaqueries.phablet`
      width: ${(400 / 1140) * 100}%;
    `}

    ${mediaqueries.phone`
      width: 90px
    `}
  }
`;

const FooterSpacer = styled.div`
  margin-bottom: 65px;
`;

const TopBanner = styled.div`
  display: flex;
  justify-content: center;
  cursor: pointer;
  margin-top: 60px;
`;

const SideBanner = styled.div`
  cursor: pointer;
  position: absolute;
  left: 80%;
  top: 30px;
  width: 18%;
  z-index: 99;
`;
